import {
  type HotelsAmenityIconSet as CoreHotelsAmenityIconSet,
  defaultHotelAmenities,
  type IconType,
} from "@b2bportal/core-themes";
import { AmenityKind } from "@b2bportal/homes-api";
import { Amenity } from "@b2bportal/lodging-api";
import { WashingMachine } from "lucide-react";

// RBC amenity icons
import RbcAccessibility from "../assets/icons/amenities/accessibility.svg?react";
import RbcAdultsOnly from "../assets/icons/amenities/adults-only.svg?react";
import RbcAirConditioning from "../assets/icons/amenities/air-conditioning.svg?react";
import RbcAirportTransportation from "../assets/icons/amenities/airport-transportation.svg?react";
import RbcArcade from "../assets/icons/amenities/arcade.svg?react";
import RbcAtmBanking from "../assets/icons/amenities/atm-banking.svg?react";
import RbcBar from "../assets/icons/amenities/bar.svg?react";
import RbcBarbecueGrill from "../assets/icons/amenities/barbecue-grill.svg?react";
import RbcBeach from "../assets/icons/amenities/beach.svg?react";
import RbcBigBath from "../assets/icons/amenities/big-bath.svg?react";
import RbcBraille from "../assets/icons/amenities/braille.svg?react";
import RbcBreakfastIncluded from "../assets/icons/amenities/breakfast-included.svg?react";
import RbcBusinessFacilities from "../assets/icons/amenities/business-facilities.svg?react";
import RbcCasino from "../assets/icons/amenities/casino.svg?react";
import RbcCoffeeMaker from "../assets/icons/amenities/coffee-maker.svg?react";
import RbcCoffee from "../assets/icons/amenities/coffee.svg?react";
import RbcComputerStation from "../assets/icons/amenities/computer-station.svg?react";
import RbcConciergeServices from "../assets/icons/amenities/concierge-services.svg?react";
import RbcDryCleaning from "../assets/icons/amenities/dry-cleaning.svg?react";
import RbcElectricCarCharging from "../assets/icons/amenities/electric-car-charging.svg?react";
import RbcEntertainment from "../assets/icons/amenities/entertainment.svg?react";
import RbcFireplace from "../assets/icons/amenities/fireplace.svg?react";
import RbcFitnessCenter from "../assets/icons/amenities/fitness-center.svg?react";
import RbcFullServiceSpa from "../assets/icons/amenities/full-service-spa.svg?react";
import RbcGarden from "../assets/icons/amenities/garden.svg?react";
import RbcGolf from "../assets/icons/amenities/golf.svg?react";
import RbcHairDryer from "../assets/icons/amenities/hair-dryer.svg?react";
import RbcHairSaloon from "../assets/icons/amenities/hair-saloon.svg?react";
import RbcHeating from "../assets/icons/amenities/heating.svg?react";
import RbcHikingOrBikingNearby from "../assets/icons/amenities/hiking-or-biking-nearby.svg?react";
import RbcHomeEssentials from "../assets/icons/amenities/home-essentials.svg?react";
import RbcHomeInfo from "../assets/icons/amenities/home-info.svg?react";
import RbcHotSpring from "../assets/icons/amenities/hot-spring.svg?react";
import RbcHotTub from "../assets/icons/amenities/hot-tub.svg?react";
import RbcIndoorPool from "../assets/icons/amenities/indoor-pool.svg?react";
import RbcJapaneseStyle from "../assets/icons/amenities/japanese-style.svg?react";
import RbcKidsFriendly from "../assets/icons/amenities/kids-friendly.svg?react";
import RbcKitchenEssentials from "../assets/icons/amenities/kitchen-essentials.svg?react";
import RbcLaundry from "../assets/icons/amenities/laundry.svg?react";
import RbcLibrary from "../assets/icons/amenities/library.svg?react";
import RbcLuggageStorage from "../assets/icons/amenities/luggage-storage.svg?react";
import RbcMeetingRooms from "../assets/icons/amenities/meeting-rooms.svg?react";
import RbcOutdoorSpace from "../assets/icons/amenities/outdoor-space.svg?react";
import RbcParking from "../assets/icons/amenities/parking.svg?react";
import RbcPetFriendly from "../assets/icons/amenities/pet-friendly.svg?react";
import RbcPetsOnlyCats from "../assets/icons/amenities/pets-only-cats.svg?react";
import RbcPetsOnlyDogs from "../assets/icons/amenities/pets-only-dogs.svg?react";
import RbcPool from "../assets/icons/amenities/pool.svg?react";
import RbcPoolsideBar from "../assets/icons/amenities/poolside-bar.svg?react";
import RbcPrivateBath from "../assets/icons/amenities/private-bath.svg?react";
import RbcRestaurant from "../assets/icons/amenities/restaurant.svg?react";
import RbcRollInShower from "../assets/icons/amenities/roll-in-shower.svg?react";
import RbcSafeDepositBox from "../assets/icons/amenities/safe-deposit-box.svg?react";
import RbcSauna from "../assets/icons/amenities/sauna.svg?react";
import RbcSmokeFree from "../assets/icons/amenities/smoke-free.svg?react";
import RbcSnack from "../assets/icons/amenities/snack.svg?react";
import RbcSpa from "../assets/icons/amenities/spa.svg?react";
import RbcTennisCourt from "../assets/icons/amenities/tennis-court.svg?react";
import RbcToursTicket from "../assets/icons/amenities/tours-ticket.svg?react";
import RbcValet from "../assets/icons/amenities/valet.svg?react";
import RbcWaterPark from "../assets/icons/amenities/water-park.svg?react";
import RbcWifi from "../assets/icons/amenities/wifi.svg?react";

export type HotelsAmenityIconSet = CoreHotelsAmenityIconSet & {
  [key in AmenityKind]?: IconType;
};

export const hotelAmenitiesIcon: HotelsAmenityIconSet = {
  ...defaultHotelAmenities,

  // RBC icon overrides
  [Amenity.Accessibility]: RbcAccessibility,
  [Amenity.AccessibleBathroom]: RbcAccessibility,
  [Amenity.InRoomAccessibility]: RbcAccessibility,
  [Amenity.SomeAccessibilityFeaturesAvailableGeneral]: RbcAccessibility,
  [Amenity.WheelchairAccessibleParking]: RbcAccessibility,
  [Amenity.WheelchairAccessiblePathOfTravel]: RbcAccessibility,
  [Amenity.AdultsOnly]: RbcAdultsOnly,
  [Amenity.AirConditioning]: RbcAirConditioning,
  [Amenity.AirportTransportation]: RbcAirportTransportation,
  [Amenity.AirportTransportationAvailableGeneral]: RbcAirportTransportation,
  [Amenity.FreeAirportTransportation]: RbcAirportTransportation,
  [Amenity.Arcade]: RbcArcade,
  [Amenity.AtmBanking]: RbcAtmBanking,
  [Amenity.Bar]: RbcBar,
  [Amenity.BarOrLounge]: RbcBar,
  [Amenity.NumberOfBarsOrLounges]: RbcBar,
  [Amenity.BarbecueGrill]: RbcBarbecueGrill,
  [Amenity.Beach]: RbcBeach,
  [Amenity.BeachEssentials]: RbcBeach,
  [Amenity.BrailleOrRaisedSignage]: RbcBraille,
  [Amenity.FreeBreakfast]: RbcBreakfastIncluded,
  [Amenity.PaidBreakfast]: RbcBreakfastIncluded,
  [Amenity.BusinessFacilities]: RbcBusinessFacilities,
  [Amenity.BusinessCenter]: RbcBusinessFacilities,
  [Amenity.Casino]: RbcCasino,
  [Amenity.CoffeeMaker]: RbcCoffeeMaker,
  [Amenity.CoffeeTea]: RbcCoffee,
  [Amenity.ComputerStation]: RbcComputerStation,
  [Amenity.ConciergeServices]: RbcConciergeServices,
  [Amenity.DryCleaningLaundryService]: RbcDryCleaning,
  [Amenity.ElectricCarCharging]: RbcElectricCarCharging,
  [Amenity.Entertainment]: RbcEntertainment,
  [Amenity.FitnessCenter]: RbcFitnessCenter,
  [Amenity.FitnessFacilities]: RbcFitnessCenter,
  [Amenity.TwentyFourHourFitnessFacilities]: RbcFitnessCenter,
  [Amenity.FullServiceSpa]: RbcFullServiceSpa,
  [Amenity.Garden]: RbcGarden,
  [Amenity.Golf]: RbcGolf,
  [Amenity.HairDryer]: RbcHairDryer,
  [Amenity.HairSalon]: RbcHairSaloon,
  [Amenity.Heating]: RbcHeating,
  [Amenity.HikingOrBikingNearby]: RbcHikingOrBikingNearby,
  [Amenity.HomeEssentials]: RbcHomeEssentials,
  [Amenity.HomeInfo]: RbcHomeInfo,
  [Amenity.HotTub]: RbcHotTub,
  [Amenity.IndoorPool]: RbcIndoorPool,
  [Amenity.KidFriendly]: RbcKidsFriendly,
  [Amenity.Childcare]: RbcKidsFriendly,
  [Amenity.KitchenEssentials]: RbcKitchenEssentials,
  [Amenity.Laundry]: RbcLaundry,
  [Amenity.LaundryFacilities]: RbcLaundry,
  [Amenity.Library]: RbcLibrary,
  [Amenity.LuggageStorage]: RbcLuggageStorage,
  [Amenity.MeetingRooms]: RbcMeetingRooms,
  [Amenity.ConferenceSpace]: RbcMeetingRooms,
  [Amenity.OutdoorSpace]: RbcOutdoorSpace,
  [Amenity.Terrace]: RbcOutdoorSpace,
  [Amenity.Parking]: RbcParking,
  [Amenity.ParkingAvailableGeneral]: RbcParking,
  [Amenity.FreeSelfParking]: RbcParking,
  [Amenity.PaidSelfParking]: RbcParking,
  [Amenity.PetFriendly]: RbcPetFriendly,
  [Amenity.PetsAllowed]: RbcPetFriendly,
  [Amenity.PetsAllowedNoFees]: RbcPetFriendly,
  [Amenity.PetsOnlyDogsAndCats]: RbcPetFriendly,
  [Amenity.PetsOnlyCats]: RbcPetsOnlyCats,
  [Amenity.PetsOnlyDogs]: RbcPetsOnlyDogs,
  [Amenity.Pool]: RbcPool,
  [Amenity.PoolGeneral]: RbcPool,
  [Amenity.OutdoorPool]: RbcPool,
  [Amenity.OutdoorSeasonalPool]: RbcPool,
  [Amenity.SwimmingPool]: RbcPool,
  [Amenity.PoolsideBar]: RbcPoolsideBar,
  [Amenity.NumberOfRestaurants]: RbcRestaurant,
  [Amenity.Restaurant]: RbcRestaurant,
  [Amenity.RollInShower]: RbcRollInShower,
  [Amenity.SafeDepositBoxFrontDesk]: RbcSafeDepositBox,
  [Amenity.Sauna]: RbcSauna,
  [Amenity.SteamRoom]: RbcSauna,
  [Amenity.SnackOrDeli]: RbcSnack,
  [Amenity.Spa]: RbcSpa,
  [Amenity.SpaTreatmentRoom]: RbcSpa,
  [Amenity.Tennis]: RbcTennisCourt,
  [Amenity.ToursTicketAssistance]: RbcToursTicket,
  [Amenity.PaidValetParking]: RbcValet,
  [Amenity.WaterPark]: RbcWaterPark,
  [Amenity.FreeWifi]: RbcWifi,
  [Amenity.RoomWifi]: RbcWifi,
  [Amenity.Nightclub]: RbcBar,
  [Amenity.PorterBellhop]: RbcConciergeServices,
  [Amenity.RoundTheClockCheckIn]: RbcConciergeServices,
  [Amenity.ExpressCheckIn]: RbcConciergeServices,
  [Amenity.TwentyFourHourFrontDesk]: RbcConciergeServices,
  [Amenity.Workspace]: RbcComputerStation,
  [Amenity.SmokeFreeProperty]: RbcSmokeFree,

  // Homes amenity overrides
  [AmenityKind.Kitchen]: RbcKitchenEssentials,
  [AmenityKind.Dryer]: WashingMachine,
  [AmenityKind.Washer]: WashingMachine,
};
